import { batch, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type AdminGrant, type GrantSubject, useAdminGrants } from '../lib/adminGrants';
import { createGrantActions } from '../lib/grant-actions';
import { usePagedQuery } from '../lib/paged';
import { AdminGrantIssueForm } from './AdminGrantIssueForm';
import { AdminGrantSubjectPicker } from './AdminGrantSubjectPicker';
import { AdminGrantsTable } from './AdminGrantsTable';
import { ConfirmDialog } from './ConfirmDialog';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { Toast } from './Toast';

// Entitlement grants (epic #1259) — the human door onto `/v1/admin/grants` and
// the MCP `grant` verb. CONTAINER: it owns the picked subject, the read and the
// re-reads; the picker, the form and the table paint.
//
// SUBJECT FIRST, ALWAYS. There is no account-wide grant listing to render: the
// read is `(subject_kind, subject_id)` and both halves are required, because the
// two axes share one column and defaulting either would answer about somebody
// else. So nothing is fetched until a subject is picked, and the page says so
// rather than showing an empty table that means nothing.
//
// IT LOADS MORE. `GET /v1/admin/grants` mints a real offset cursor, so paging is
// `usePagedQuery` and the button means what it says. The old "showing the first
// 100" footer is gone with the truncation it described: a labelled cut-short list
// is the right answer only while page two is unreachable.
//
// BOTH WRITES RE-READ. Issue and revoke each answer with a grant, but the list
// resolves `active` for every row at one server-side instant; splicing an echoed
// row in would leave one row on the page evaluated against a different clock.
// `reload()` rather than `refetch()`: past page one the server's slice shifts
// under a held offset cursor, so the deeper pages stop lining up with page one.
//
// A SUBJECT CHANGE DROPS BOTH A PENDING REVOKE AND THE ACCUMULATED PAGES, in ONE
// batched write. The confirm dialog is mounted outside the subject's `<Show/>`,
// so re-picking behind it would leave it asking about a row no longer on the page
// — and Confirm would then revoke something invisible. The rows have to go for
// the same reason one level down: an OFFSET cursor belongs to the subject it was
// minted under, so carrying it across reads past the new subject's first page and
// renders an org that holds grants as one that holds none.
//
// One writer, not an effect on the signal. `createEffect(on(subject, …))` runs
// AFTER the query has already re-keyed to `(new subject, old cursor)` and fired,
// so the reset only cleans up behind a read that was wrong when it went out.
// `changeSubject` is the ONLY way `subject` moves, which is what makes the rule
// hold by construction — the `<AdminOrgsPanel/>` search batch, one screen over.
//
// THE ROUTE MAY HAND IN THE FIRST PICK. `initialSubject` is what
// `/admin/grants?subject_kind=…&subject_id=…` resolves to (`routes/AdminGrants.tsx`,
// `grantSubjectFromParams`): the user registry's Grant link passes the person it
// already resolved, the one shape that cannot be mistyped, so the operator is
// not asked to search for a name they were just looking at. Read ONCE at mount;
// the picker owns the subject from there. A prop rather than a `useSearchParams`
// here, so this container still unit-tests without a Router.

export function AdminGrantsPanel(props: {
  readonly token: string | null;
  readonly initialSubject?: GrantSubject | null;
}): JSX.Element {
  const [subject, setSubject] = createSignal<GrantSubject | null>(props.initialSubject ?? null);
  const list = usePagedQuery<AdminGrant>((cursor) => useAdminGrants(subject, cursor, props.token));
  const actions = createGrantActions({
    token: props.token,
    onWritten: () => list.reload(),
  });

  const changeSubject = (next: GrantSubject | null): void =>
    batch(() => {
      setSubject(next);
      list.reset();
      actions.cancel();
    });

  return (
    <>
      <PageHeader title={t('admin.grants_title')} desc={t('admin.grants_subtitle')} />
      <OperatorRefusalAlert refusal={actions.refusal()} />

      <AdminGrantSubjectPicker
        token={props.token}
        subject={subject()}
        onPick={changeSubject}
        onClear={() => changeSubject(null)}
      />

      <Show
        when={subject()}
        fallback={<EmptyState message={t('admin.grants_pick_subject')} />}
        keyed
      >
        {(picked) => (
          <>
            <Panel title={t('admin.grants_issue_title', { subject: picked.label })}>
              <AdminGrantIssueForm
                subject={picked}
                busy={actions.issuing()}
                onIssue={actions.issue}
              />
            </Panel>

            {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
            <QueryBoundary
              query={list.view}
              isEmpty={(rows) => rows.length === 0}
              loading={<Skeleton height="10rem" />}
              empty={<EmptyState message={t('admin.grants_empty', { subject: picked.label })} />}
            >
              {(rows) => (
                <Panel title={t('admin.grants_history_title', { subject: picked.label })}>
                  <AdminGrantsTable
                    rows={rows}
                    isBusy={actions.isBusy}
                    onRevoke={actions.askRevoke}
                  />
                  <LoadMore list={list} label={t('admin.load_more')} />
                </Panel>
              )}
            </QueryBoundary>
          </>
        )}
      </Show>

      <ConfirmDialog
        open={actions.pending() !== null}
        title={t('admin.grants_revoke_title', { kind: actions.pending()?.kind ?? '' })}
        desc={t('admin.grants_revoke_desc')}
        cancelLabel={t('admin.grants_cancel')}
        actionLabel={t('admin.grants_revoke')}
        destructive
        onCancel={actions.cancel}
        onConfirm={actions.confirm}
      />
      <Toast
        message={actions.error() ?? actions.notice()}
        tone={actions.error() !== null ? 'error' : 'warning'}
        onDismiss={() => {
          actions.clearError();
          actions.clearNotice();
        }}
      />
    </>
  );
}
